import type { Locale } from '@et/schema';
import { toApiQuery, type InvestigationFilters } from './filters.js';

export interface ApiMeta {
  readonly contentVersion: number;
  readonly checksum: string;
  readonly canonicalLocale: Locale;
  readonly locales: readonly Locale[];
  readonly counts: Record<string, number>;
}

export interface EvidenceListItem {
  readonly id: string;
  readonly title: string;
  readonly evidenceType: string;
  readonly event: readonly [number, number];
  readonly discovery: readonly [number, number] | null;
  readonly strength: string;
  readonly sourceLayer: string;
  readonly disputed: boolean;
  readonly fallbackFields: readonly string[];
}

/** Um `SourcedText` como o SQLite o guarda: prosa mais as fontes que a sustentam. */
export interface ApiSourcedText {
  readonly text: string;
  readonly source_ids: readonly string[];
  readonly epistemic_kind: string;
}

export interface ApiSpan {
  readonly label: string;
  readonly earliest: number;
  readonly latest: number;
  readonly disputed: boolean;
}

export interface EvidenceDetail {
  readonly id: string;
  readonly title: string;
  readonly evidenceType: string;
  readonly event: ApiSpan;
  readonly discovery: ApiSpan | null;
  readonly location: { name: string } | null;
  readonly currentLocation:
    { institution: string; inventory_number?: string | null; url?: string | null } | null;
  readonly whatWeKnow: ApiSourcedText | null;
  readonly howWeKnowIt: ApiSourcedText | null;
  /** I2 — a API devolve sempre as duas listas; um card sem elas nao pode ser
      desenhado. O alcance e o limite andam sempre a par. */
  readonly whatItDoesProve: readonly ApiSourcedText[];
  readonly whatItDoesNotProve: readonly ApiSourcedText[];
  readonly strength: string;
  readonly consensus: string;
  readonly sourceLayer: string;
  readonly sources: readonly {
    id: string; title: string; authors: readonly string[]; academicLevel: string;
    perspective: string; primaryOrSecondary: string; language: string; access: string;
    url: string | null; publication: string | null;
    excerpts: readonly { text: string; locus?: string }[];
  }[];
  /** §113 — testemunhos, nao documentos. Vem calculado do servidor. */
  readonly clusters: readonly { root: string; sourceIds: readonly string[]; reason: string | null }[];
  readonly relations: readonly {
    kind: string; toType: string; toId: string; title: string | null;
    significance: string | null; rationale: string | null;
  }[];
  readonly media: readonly ApiMedia[];
  readonly fallbackFields: readonly string[];
}

/** A placa como o servidor a devolve — a ficha e a licao desenham a mesma. */
export interface ApiMedia {
  readonly id: string;
  readonly kind: string;
  readonly sourceUrl: string | null;
  readonly credit: string;
  readonly rightsHolder: string;
  readonly license: string;
  readonly licenseUrl: string | null;
  readonly licenseNotes: string | null;
  readonly iiifManifest: string | null;
  readonly altText: string;
  readonly caption: string | null;
}

export interface ApiPremise {
  readonly id: string;
  readonly statement: ApiSourcedText;
  readonly status: string;
  readonly evidence_ids?: readonly string[];
  readonly objection_ids?: readonly string[];
  readonly depends_on_claim_ids?: readonly string[];
}

export interface ApiResponse {
  readonly id: string;
  readonly title: string;
  readonly strength: string;
  readonly position: ApiSourcedText | null;
  readonly proponents: readonly { name: string }[];
  readonly evidenceIds: readonly string[];
  readonly counterResponses?: readonly ApiResponse[];
}

export interface ApiObjection {
  readonly id: string;
  readonly title: string;
  readonly strength: string;
  readonly religionId: string;
  readonly steelman: ApiSourcedText | null;
  readonly premises: readonly ApiPremise[];
  readonly proponents: readonly { name: string }[];
  readonly responses: readonly ApiResponse[];
}

export interface ApiArgument {
  readonly id: string;
  readonly title: string;
  readonly position: string;
  readonly religionId: string | null;
  readonly argumentType: string;
  readonly internalOrExternal: string;
  readonly strength: string;
  readonly strengthExplanation: { text: string } | null;
  readonly conclusion: ApiSourcedText | null;
  readonly premises: readonly ApiPremise[];
  readonly evidenceIds: readonly string[];
  readonly objectionIds: readonly string[];
}

export interface ClaimDetail {
  readonly id: string;
  readonly title: string;
  readonly religionId: string;
  readonly claimType: string;
  readonly foundational: boolean;
  readonly necessity: string | null;
  readonly historicalStatus: string;
  readonly statusExplanation: ApiSourcedText | null;
  readonly whatItDoesProve: readonly ApiSourcedText[];
  readonly whatItDoesNotProve: readonly ApiSourcedText[];
  readonly whatWouldChangeMyMind: {
    would_move_down?: readonly string[];
    would_move_up?: readonly string[];
    current_limitations?: readonly string[];
  } | null;
  readonly counterpartClaimId: string | null;
  readonly counterpartTitle: string | null;
  readonly counterpartRationale: string | null;
  readonly evidence: readonly { id: string; title: string; significance: string | null }[];
  readonly challenges: readonly { id: string; title: string; significance: string | null }[];
  readonly context: readonly { id: string; title: string; significance: string | null }[];
  readonly objections: readonly ApiObjection[];
  readonly arguments: readonly ApiArgument[];
  readonly topics: readonly { id: string; title: string; slug: string }[];
  readonly fallbackFields: readonly string[];
}

export interface ApiGraph {
  readonly rootId: string;
  readonly nodes: readonly {
    id: string; title: string; religionId: string;
    status: string; foundational: boolean; depth: number;
  }[];
  readonly edges: readonly { from: string; to: string; kind: string }[];
}

export interface AssumeFalseResult {
  readonly affected: readonly {
    id: string; title: string; depth: number; severity: 'critical' | 'major';
    via: readonly { from: string; to: string; kind: string }[];
  }[];
  readonly disclaimer: string;
}

export interface TopicSummary {
  readonly id: string;
  readonly title: string;
  readonly slug: string;
  readonly religionId: string | null;
  readonly summary: { text: string } | null;
  readonly fallbackFields: readonly string[];
}

export interface TopicDetail extends TopicSummary {
  readonly claims: readonly {
    id: string; title: string; religionId: string; claimType: string;
    foundational: boolean; necessity: string | null; historicalStatus: string;
    counterpartClaimId: string | null; counterpartTitle: string | null;
    fallbackFields: readonly string[];
  }[];
  readonly evidence: readonly {
    id: string; title: string; evidenceType: string; strength: string;
  }[];
}

export class StaleContentError extends Error {
  constructor(readonly current: number) { super(`content version stale, current ${current}`); }
}

import { appCheckHeader } from './app-check.js';

const base = (import.meta as { env?: Record<string, string> }).env?.['VITE_API_ORIGIN'] ?? '';

async function request<T>(path: string): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    headers: { Accept: 'application/json', ...(await appCheckHeader()) },
  });
  if (res.status === 409) {
    const body = await res.json() as { current: number };
    // O conteudo publicado avancou. O cliente recarrega em vez de servir
    // silenciosamente uma versao que ja nao existe.
    throw new StaleContentError(body.current);
  }
  if (!res.ok) throw new Error(`${res.status} ${path}`);
  return res.json() as Promise<T>;
}

export const fetchMeta = (): Promise<ApiMeta> => request<ApiMeta>('/api/meta');

export const fetchEvidence = (
  version: number, locale: Locale, filters: InvestigationFilters,
): Promise<{ items: EvidenceListItem[]; total: number }> => {
  const q = toApiQuery(filters).toString();
  return request(`/api/v${version}/${locale}/evidence${q === '' ? '' : `?${q}`}`);
};

export const fetchEvidenceDetail = (version: number, locale: Locale, id: string): Promise<EvidenceDetail> =>
  request(`/api/v${version}/${locale}/evidence/${encodeURIComponent(id)}`);

export const fetchClaim = (version: number, locale: Locale, id: string): Promise<ClaimDetail> =>
  request(`/api/v${version}/${locale}/claims/${encodeURIComponent(id)}`);

export const fetchClaimGraph = (
  version: number, locale: Locale, id: string,
): Promise<{ graph: ApiGraph }> =>
  request(`/api/v${version}/${locale}/claims/${encodeURIComponent(id)}/dependencies`);

export const fetchAssumeFalse = (
  version: number, locale: Locale, id: string,
): Promise<AssumeFalseResult> =>
  request(`/api/v${version}/${locale}/claims/${encodeURIComponent(id)}/assume-false`);

export const fetchTopics = (version: number, locale: Locale): Promise<{ items: TopicSummary[] }> =>
  request(`/api/v${version}/${locale}/topics`);

export const fetchTopic = (version: number, locale: Locale, slug: string): Promise<TopicDetail> =>
  request(`/api/v${version}/${locale}/topics/${encodeURIComponent(slug)}`);

/** §07 — o subgrafo que o motor consome. Vem inteiro, e o calculo e do cliente. */
export interface AssessmentInputResponse {
  readonly claim: {
    id: string; title: string; religionId: string;
    claimType: string; historicalStatus: string;
  };
  readonly anchorYear: number;
  readonly evidence: readonly {
    id: string; title: string; evidenceType: string; sourceLayer: string;
    strength: string; eventEarliest: number; eventLatest: number;
    disputed: boolean; sourceIds: readonly string[];
  }[];
  readonly sources: readonly {
    id: string; title: string; academicLevel: string; primaryOrSecondary: string;
  }[];
  readonly sourceLinks: readonly { from: string; to: string; kind: string }[];
  readonly supports: readonly { evidenceId: string; kind: string; significance: string | null }[];
  readonly objections: readonly {
    id: string; title: string; strength: string;
    hasSteelman: boolean; responseStrengths: readonly string[];
  }[];
  readonly arguments: readonly {
    id: string; title: string; argumentType: string; internalOrExternal: string;
    strength: string; premises: readonly { id: string; text: string; status: string }[];
  }[];
  readonly baseline: readonly { dimension: string; band: string; explanation: string }[];
}

export const fetchAssessmentInput = (
  version: number, locale: Locale, id: string,
): Promise<AssessmentInputResponse> =>
  request(`/api/v${version}/${locale}/claims/${encodeURIComponent(id)}/assessment-input`);

// ── Sacred Text Lab (§65–§71) ──────────────────────────────────
export interface CorpusRulerResponse {
  readonly id: string;
  readonly title: string;
  readonly traditionId: string;
  readonly language: string;
  readonly totalUnits: number;
  readonly divisions: readonly {
    id: string; corpusId: string; title: string; shortTitle: string; order: number;
    unitCount: number; cumulativeOffset: number; sourceLayer: string;
    analysedUnits: number; passageCount: number;
  }[];
  readonly passages: readonly {
    id: string; divisionId: string; reference: string; rulerPosition: number;
    unitStart: number; unitEnd: number; sourceLayer: string;
    textConfidence: string; interpretationConfidence: string;
    challengeCategories: readonly string[];
  }[];
}

export interface PassageArc {
  readonly from: string;
  readonly to: string;
  readonly kind: string;
  readonly rationale: string | null;
  readonly crossCorpus: boolean;
}

export interface PassageDetail {
  readonly id: string;
  readonly corpusId: string;
  readonly divisionId: string;
  readonly division: { id: string; title: string; shortTitle: string; sourceLayer: string } | null;
  readonly reference: string;
  readonly sourceLayer: string;
  readonly originalText: {
    language: string; text: string | null; omission: string | null;
    criticalEditionSourceId: string;
  };
  readonly translations: readonly {
    language: string; text: string | null; omission?: string;
    translator: string; source_id: string; notes?: string;
  }[];
  readonly translationDivergences: readonly { text: string }[];
  readonly composition: { earliest: number; latest: number };
  readonly speaker: string | null;
  readonly audience: string | null;
  readonly literaryGenre: string | null;
  readonly narrativeContext: { text: string } | null;
  readonly textConfidence: string;
  readonly interpretationConfidence: string;
  readonly challengeCategories: readonly string[];
  readonly arcs: readonly PassageArc[];
  readonly sources: readonly { id: string; title: string; academicLevel: string; language: string | null }[];
  readonly fallbackFields: readonly string[];
}

export const fetchCorpora = (
  version: number, locale: Locale,
): Promise<{ items: { id: string; title: string; traditionId: string; totalUnits: number }[] }> =>
  request(`/api/v${version}/${locale}/corpora`);

export const fetchRuler = (version: number, locale: Locale, id: string): Promise<CorpusRulerResponse> =>
  request(`/api/v${version}/${locale}/corpora/${encodeURIComponent(id)}/ruler`);

export const fetchArcs = (version: number, locale: Locale): Promise<{ arcs: PassageArc[] }> =>
  request(`/api/v${version}/${locale}/passages/arcs`);

export const fetchPassage = (version: number, locale: Locale, id: string): Promise<PassageDetail> =>
  request(`/api/v${version}/${locale}/passages/${encodeURIComponent(id)}`);

export interface TensionMatrixResponse {
  readonly id: string;
  readonly title: string;
  readonly religionId: string;
  readonly passageIds: readonly string[];
  readonly rows: readonly {
    id: string; feature: string;
    cells: readonly { passage_id: string; content: { text: string } | null }[];
    critical_reading: { text: string };
    harmonizing_reading: { text: string };
    counter_response?: { text: string };
    current_assessment: { text: string };
    source_ids: readonly string[];
  }[];
}

export const fetchTensionMatrices = (
  version: number, locale: Locale,
): Promise<{ items: TensionMatrixResponse[] }> =>
  request(`/api/v${version}/${locale}/tension-matrices`);

export interface ProphecyResponse {
  readonly id: string;
  readonly title: string;
  readonly religionId: string;
  readonly passageId: string;
  readonly preEventEvidenceIds: readonly string[];
  readonly originalContext: { text: string } | null;
  readonly claimedFulfilment: { text: string } | null;
  readonly alternativeReading: { text: string } | null;
  readonly findings: readonly {
    criterion: string; verdict: string; explanation: { text: string };
  }[];
}

export const fetchProphecies = (
  version: number, locale: Locale,
): Promise<{ items: ProphecyResponse[] }> =>
  request(`/api/v${version}/${locale}/prophecies`);

// §73–§76 — a centralidade nas figuras.
export interface CharacterProfileResponse {
  readonly figures: readonly {
    id: string; religionId: string; name: string;
    lifespan: { label?: string; earliest?: number; latest?: number } | null;
    summary: { text: string } | null;
  }[];
  readonly themes: readonly string[];
  readonly episodes: readonly {
    id: string; figureId: string; theme: string; title: string;
    attributionLevel: string; sourceLayer: string;
    passageIds: readonly string[]; evidenceIds: readonly string[];
    whatTraditionSays: { text: string } | null;
    historicalQuestion: { text: string } | null;
    historicalStatus: string;
    moralQuestion: { text: string } | null;
    contextualDefense: { text: string } | null;
    criticalResponse: { text: string } | null;
    culturalFriction: { stance: string; explanation: { text: string } } | null;
  }[];
}

export interface IdentityClaimResponse {
  readonly id: string;
  readonly figureId: string;
  readonly title: string;
  readonly sourceLayer: string;
  readonly passageIds: readonly string[];
  readonly whatTheTextSays: { text: string } | null;
  readonly criticalReading: { text: string } | null;
  readonly confessionalReading: { text: string } | null;
  readonly historicalStatus: string;
}

export const fetchCharacterProfile = (
  version: number, locale: Locale,
): Promise<CharacterProfileResponse> =>
  request(`/api/v${version}/${locale}/figures/profile`);

export interface FigureOverviewResponse {
  readonly figure: {
    id: string; religionId: string; name: string;
    lifespan: { label?: string } | null; summary: { text: string } | null;
  };
  readonly inventory: readonly {
    figureId: string; prophecies: number; identityClaims: number; episodes: number;
    themes: number; passages: number; strongObjections: number; boldClaims: number;
  }[];
  readonly figures: readonly { id: string; name: string; religionId: string }[];
  readonly passages: readonly {
    id: string; corpusId: string; divisionId: string; reference: string;
    rulerPosition: number; unitStart: number; unitEnd: number; sourceLayer: string;
  }[];
  readonly prophecies: readonly ProphecyResponse[];
  readonly identityClaims: readonly IdentityClaimResponse[];
  readonly episodes: CharacterProfileResponse['episodes'];
  readonly themes: readonly string[];
  readonly objections: readonly {
    id: string; title: string; strength: string; claimId: string; claimTitle: string;
  }[];
}

export const fetchFigure = (
  version: number, locale: Locale, id: string,
): Promise<FigureOverviewResponse> =>
  request(`/api/v${version}/${locale}/figures/${encodeURIComponent(id)}`);

export const fetchIdentityClaims = (
  version: number, locale: Locale,
): Promise<{ items: IdentityClaimResponse[] }> =>
  request(`/api/v${version}/${locale}/figures/identity-claims`);

// ── Compare (§90–§101) ─────────────────────────────────────────
export interface ComparisonPositionResponse {
  readonly religion_id: string;
  readonly summary: { text: string } | null;
  readonly claim_ids: readonly string[];
  readonly argument_ids: readonly string[];
  readonly evidence_ids: readonly string[];
  readonly objection_ids: readonly string[];
  readonly response_ids: readonly string[];
  readonly passage_ids: readonly string[];
}

export interface ComparisonDimensionResponse {
  readonly dimension: string;
  readonly bands: readonly { religion_id: string; band: string }[];
  readonly uncertainty: string;
  readonly not_applicable_reason?: { text: string };
  readonly rationale?: { text: string };
}

export interface ComparisonSummary {
  readonly fallbackFields?: readonly string[];
  readonly id: string;
  readonly title: string;
  readonly category: string;
  readonly featured: boolean;
  readonly proposition: { text: string } | null;
  readonly commonGround: readonly { text: string }[];
  readonly divergencePoint: { text: string } | null;
  readonly positions: readonly ComparisonPositionResponse[];
  readonly dimensions: readonly ComparisonDimensionResponse[];
  readonly whatHistoryEstablishes: { text: string } | null;
  readonly mainUnresolvedIssue: { text: string } | null;
  readonly evidenceIds: readonly string[];
  readonly biasAudit: {
    checks: Record<string, boolean>; auditors: readonly string[];
    audited_at?: string; notes?: { text: string };
  } | null;
}

export interface ComparisonDetail extends ComparisonSummary {
  readonly evidence: readonly {
    id: string; title: string; evidenceType: string; strength: string;
    sourceLayer: string; event: readonly [number, number];
    religionIds: readonly string[];
  }[];
  readonly religions: readonly { id: string; name: string }[];
  /** Título de tudo o que as pilhas nomeiam, para não se mostrarem ids. */
  readonly titles: Readonly<Record<string, string>>;
}

export const fetchComparisons = (
  version: number, locale: Locale,
): Promise<{ items: ComparisonSummary[] }> =>
  request(`/api/v${version}/${locale}/comparisons`);

export const fetchComparison = (
  version: number, locale: Locale, id: string,
): Promise<ComparisonDetail> =>
  request(`/api/v${version}/${locale}/comparisons/${encodeURIComponent(id)}`);

// ── Play Mode (§31–§42) ────────────────────────────────────────
export interface PlayStepResponse {
  readonly id: string;
  readonly order: number;
  readonly title: string;
  readonly step_type: string;
  readonly epistemic_status: string;
  readonly short_explanation: { text: string } | null;
  readonly status_explanation: { text: string } | null;
  readonly why_it_matters: { text: string } | null;
  readonly what_it_does_prove: { text: string } | null;
  readonly what_it_does_not_prove: { text: string } | null;
  readonly claim_ids: readonly string[];
  readonly evidence_ids: readonly string[];
  readonly objection_ids: readonly string[];
  readonly response_ids: readonly string[];
  readonly passage_ids: readonly string[];
  readonly comparison_ids: readonly string[];
  readonly source_ids: readonly string[];
  readonly next_step_ids: readonly string[];
  readonly branch_options: readonly { label: string; next_step_id: string; kind: string }[];
}

export interface PlaySummaryResponse {
  readonly id: string;
  readonly title: string;
  readonly mode: string;
  readonly description: { text: string } | null;
  readonly religionIds: readonly string[];
  readonly topicIds: readonly string[];
  readonly claimIds: readonly string[];
  readonly evidenceIds: readonly string[];
  readonly passageIds: readonly string[];
  readonly comparisonIds: readonly string[];
  readonly stepCount: number;
  /** Uma peça que a própria sequência percorre, ou `null`. */
  readonly media?: ApiMedia | null;
}

export interface PlayDetail extends Omit<PlaySummaryResponse, 'stepCount'> {
  readonly steps: readonly PlayStepResponse[];
  readonly summary: {
    byStatus: Record<string, number>;
    steps: number;
    sourcesReviewed: number;
    primarySources: number;
  };
}

export const fetchPlays = (
  version: number, locale: Locale,
): Promise<{ items: PlaySummaryResponse[] }> =>
  request(`/api/v${version}/${locale}/play`);

export const fetchPlay = (version: number, locale: Locale, id: string): Promise<PlayDetail> =>
  request(`/api/v${version}/${locale}/play/${encodeURIComponent(id)}`);

/** §36 — que investigações guiadas partem desta entidade. */
export const fetchPlaysFor = (
  version: number, locale: Locale, entityId: string,
): Promise<{ items: { id: string; title: string }[] }> =>
  request(`/api/v${version}/${locale}/play/for/${encodeURIComponent(entityId)}`);

// ── Courses (§44-§62) ──────────────────────────────────────────
export interface CourseSummary {
  readonly fallbackFields?: readonly string[];
  readonly id: string;
  readonly title: string;
  readonly track: string | null;
  readonly religionIds: readonly string[];
  readonly difficulty: string;
  readonly version: number;
  readonly description: { text: string } | null;
  readonly moduleCount: number;
  readonly lessonCount: number;
  readonly checkpointCount: number;
  readonly sourceCount: number;
  /** A peca que o curso ensina, ou `null` quando nenhuma tem imagem mostravel. */
  readonly media: ApiMedia | null;
}

export interface CourseLessonRef {
  readonly id: string;
  readonly order: number;
  readonly title: string;
  readonly estimatedReading: string;
  readonly learningObjectives: readonly string[];
  readonly checkpoints: number;
}

export interface CourseDetail extends Omit<CourseSummary, 'moduleCount' | 'lessonCount' | 'checkpointCount'> {
  readonly prerequisiteCourseIds: readonly string[];
  readonly modules: readonly {
    id: string; order: number; title: string; theme: string;
    lessons: readonly CourseLessonRef[];
  }[];
  readonly lessonIds: readonly string[];
}

export interface CheckpointItemResponse {
  readonly id: string;
  readonly kind: string;
  readonly competence: string;
  readonly prompt: string;
  readonly scored: boolean;
  readonly explanation: { text: string; source_ids: readonly string[] } | null;
  readonly relatedEntityRefs: readonly { type: string; id: string }[];
  readonly options?: readonly { id: string; text: string }[];
  readonly correct_option_ids?: readonly string[];
  readonly statement?: string;
  readonly answer?: boolean;
  readonly pairs?: readonly { source_id: string; claim_id: string }[];
  readonly events?: readonly { id: string; label: string; year: number }[];
  readonly statements?: readonly { id: string; text: string; layer?: string; historical?: boolean }[];
  readonly argument_ids?: readonly string[];
  readonly what_a_good_answer_shows?: readonly string[];
}

export type LessonBlockResponse =
  | { kind: 'prose'; content: { text: string; source_ids: readonly string[] } }
  | { kind: 'entity_ref'; ref: { type: string; id: string }; display: string; note?: string }
  | { kind: 'play_embed'; play_sequence_id: string }
  | { kind: 'comparison_embed'; comparison_question_id: string }
  | { kind: 'fact_vs_interpretation'; items: readonly { id: string; text: string; layer: string }[] }
  | { kind: 'checkpoint'; checkpoint_item_ids: readonly string[] };

export interface LessonDetail {
  readonly fallbackFields?: readonly string[];
  readonly id: string;
  readonly title: string;
  readonly order: number;
  readonly estimatedReading: string;
  readonly learningObjectives: readonly string[];
  readonly blocks: readonly LessonBlockResponse[];
  readonly topicIds: readonly string[];
  readonly claimIds: readonly string[];
  readonly evidenceIds: readonly string[];
  readonly passageIds: readonly string[];
  readonly playSequenceIds: readonly string[];
  readonly objectionIds: readonly string[];
  readonly comparisonIds: readonly string[];
  readonly sourceIds: readonly string[];
  readonly checkpointItems: readonly CheckpointItemResponse[];
  readonly module: { id: string; title: string; theme: string; order: number };
  readonly course: { id: string; title: string; version: number; lessonCount: number };
  readonly position: number;
  readonly previousLessonId: string | null;
  readonly nextLessonId: string | null;
  readonly titles: Record<string, string>;
  /** §51 — a imagem da peca citada, indexada por `evidence:<id>`. */
  readonly media?: Record<string, ApiMedia>;
}

export const fetchCourses = (
  version: number, locale: Locale,
): Promise<{ items: CourseSummary[] }> => request(`/api/v${version}/${locale}/courses`);

export const fetchCourse = (
  version: number, locale: Locale, id: string,
): Promise<CourseDetail> => request(`/api/v${version}/${locale}/courses/${encodeURIComponent(id)}`);

export const fetchLesson = (
  version: number, locale: Locale, id: string,
): Promise<LessonDetail> => request(`/api/v${version}/${locale}/lessons/${encodeURIComponent(id)}`);

/** §49 — que licoes tocam este topico. */
export const fetchTopicLessons = (
  version: number, locale: Locale, slug: string,
): Promise<{ items: { id: string; title: string; courseId: string; courseTitle: string }[] }> =>
  request(`/api/v${version}/${locale}/topics/${encodeURIComponent(slug)}/lessons`);

// ── cobertura pública (§16 §6, [09 §7]) ────────────────────────
export interface Coverage {
  readonly divisions: readonly {
    divisionId: string; shortTitle: string; corpusId: string;
    analysedUnits: number; totalUnits: number; passageCount: number;
  }[];
  readonly locales: readonly {
    locale: string; entityType: string; tier: number; translated: number; total: number;
  }[];
}

export const fetchCoverage = (version: number): Promise<Coverage> =>
  request(`/api/v${version}/coverage`);

export interface Facets {
  readonly religions: { id: string; name: string; slug: string }[];
  readonly topics: { id: string; religionId: string | null; title: string; slug: string }[];
  readonly evidenceTypes: { id: string; n: number }[];
  readonly sourceLayers: { id: string; n: number }[];
}

export const fetchFacets = (version: number, locale: Locale): Promise<Facets> =>
  request(`/api/v${version}/${locale}/facets`);

export interface Ledger {
  readonly evidence: number;
  readonly perReligion: readonly { religionId: string; n: number }[];
  readonly claims: number;
  readonly passages: number;
  readonly objections: {
    readonly total: number; readonly strong: number;
    readonly responded: number; readonly defeated: number;
  };
}
export const fetchLedger = (version: number, locale: Locale): Promise<Ledger> =>
  request(`/api/v${version}/${locale}/ledger`);

export interface ObjectionListItem {
  readonly id: string;
  readonly title: string;
  readonly titleIsFallback: boolean;
  readonly targetType: string;
  readonly targetId: string;
  readonly targetTitle: string | null;
  readonly religionId: string;
  readonly strength: string;
  readonly steelman: { text: string; source_ids: string[] } | null;
  readonly proponents: readonly { name: string }[];
  readonly responses: number;
  /** O objecto em disputa, quando a objecção tem um e ele pode mostrar-se. */
  readonly media?: ApiMedia | null;
}
export const fetchObjections = (version: number, locale: Locale): Promise<{ items: ObjectionListItem[] }> =>
  request(`/api/v${version}/${locale}/objections`);

export interface SearchHit {
  readonly type: 'evidence' | 'claim' | 'passage' | 'objection' | 'topic' | 'source';
  readonly id: string;
  readonly title: string;
  readonly detail: string;
  /** Onde casou, quando casou no corpo e nao no titulo. */
  readonly snippet: string | null;
}
export const fetchSearch = (version: number, locale: Locale, q: string): Promise<{ items: SearchHit[] }> =>
  request(`/api/v${version}/${locale}/search?q=${encodeURIComponent(q)}`);

/**
 * Que conteudo esta a ser servido. A versao sozinha nao chega: dentro de uma
 * versao o conteudo pode ser reconstruido (e em desenvolvimento e-o a cada
 * edicao no backoffice), e uma chave que so olhasse para a versao serviria a
 * prosa antiga para sempre — `staleTime: Infinity` garante-o. O checksum e
 * exactamente "que conteudo e este", por isso entra na chave.
 */
export interface ContentRev { readonly version: number; readonly checksum: string; }

const rev = (r: ContentRev) => `${r.version}@${r.checksum}`;

/**
 * Chaves de cache. O conteudo e imutavel para um dado checksum, logo
 * `staleTime: Infinity` e correcto — e uma reconstrucao invalida tudo de uma
 * vez, sem ninguem ter de se lembrar de o fazer.
 */
export const queryKeys = {
  meta: ['meta'] as const,
  evidence: (r: ContentRev, l: Locale, f: InvestigationFilters) =>
    ['evidence', rev(r), l, toApiQuery(f).toString()] as const,
  evidenceDetail: (r: ContentRev, l: Locale, id: string) => ['evidence', rev(r), l, id] as const,
  facets: (r: ContentRev, l: Locale) => ['facets', rev(r), l] as const,
  claim: (r: ContentRev, l: Locale, id: string) => ['claim', rev(r), l, id] as const,
  claimGraph: (r: ContentRev, l: Locale, id: string) => ['claim-graph', rev(r), l, id] as const,
  assumeFalse: (r: ContentRev, l: Locale, id: string) => ['assume-false', rev(r), l, id] as const,
  topics: (r: ContentRev, l: Locale) => ['topics', rev(r), l] as const,
  topic: (r: ContentRev, l: Locale, slug: string) => ['topic', rev(r), l, slug] as const,
  assessmentInput: (r: ContentRev, l: Locale, id: string) =>
    ['assessment-input', rev(r), l, id] as const,
  corpora: (r: ContentRev, l: Locale) => ['corpora', rev(r), l] as const,
  ruler: (r: ContentRev, l: Locale, id: string) => ['ruler', rev(r), l, id] as const,
  arcs: (r: ContentRev, l: Locale) => ['arcs', rev(r), l] as const,
  passage: (r: ContentRev, l: Locale, id: string) => ['passage', rev(r), l, id] as const,
  tensionMatrices: (r: ContentRev, l: Locale) => ['tension-matrices', rev(r), l] as const,
  prophecies: (r: ContentRev, l: Locale) => ['prophecies', rev(r), l] as const,
  characterProfile: (r: ContentRev, l: Locale) => ['character-profile', rev(r), l] as const,
  identityClaims: (r: ContentRev, l: Locale) => ['identity-claims', rev(r), l] as const,
  figure: (r: ContentRev, l: Locale, id: string) => ['figure', rev(r), l, id] as const,
  comparisons: (r: ContentRev, l: Locale) => ['comparisons', rev(r), l] as const,
  comparison: (r: ContentRev, l: Locale, id: string) => ['comparison', rev(r), l, id] as const,
  plays: (r: ContentRev, l: Locale) => ['plays', rev(r), l] as const,
  play: (r: ContentRev, l: Locale, id: string) => ['play', rev(r), l, id] as const,
  playsFor: (r: ContentRev, l: Locale, entityId: string) =>
    ['plays-for', rev(r), l, entityId] as const,
  courses: (r: ContentRev, l: Locale) => ['courses', rev(r), l] as const,
  course: (r: ContentRev, l: Locale, id: string) => ['course', rev(r), l, id] as const,
  lesson: (r: ContentRev, l: Locale, id: string) => ['lesson', rev(r), l, id] as const,
  topicLessons: (r: ContentRev, l: Locale, slug: string) =>
    ['topic-lessons', rev(r), l, slug] as const,
  coverage: (r: ContentRev) => ['coverage', rev(r)] as const,
  ledger: (r: ContentRev, l: Locale) => ['ledger', rev(r), l] as const,
  objections: (r: ContentRev, l: Locale) => ['objections', rev(r), l] as const,
  search: (r: ContentRev, l: Locale, q: string) => ['search', rev(r), l, q] as const,
};
